// src/components/Pricing.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, addDoc, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../firebase/firebase';
import { loadStripe } from '@stripe/stripe-js';
import { httpsCallable } from 'firebase/functions';
import { motion } from 'framer-motion';
import { logEventSafe } from '../analytics/analytics';
import { PageBackLink, PageShell } from './MarketingPageShell';

const stripePromise = loadStripe(
  'pk_live_51QioU8BqlgUftOgOwYTgp39DIyppsWz7dviz2vVcy5ICqaLqZcoEs4y1jEofftIOeSetuP8Sx5ypJEWKlDXCkUS900tT0djnS7'
);

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { when: 'beforeChildren', staggerChildren: 0.1, duration: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 80, damping: 12 } },
};

const Pricing = () => {
  const auth = getAuth();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState('standard');
  const [userRole, setUserRole] = useState(null);
  const [orgId, setOrgId] = useState('');
  const [orgMemberCount, setOrgMemberCount] = useState(1);
  const [loadingAction, setLoadingAction] = useState(null);

  useEffect(() => {
    logEventSafe('page_view', {
      page_path: location.pathname,
      page_title: 'Pricing'
    });
  }, [location.pathname]);

  useEffect(() => {
    const warm = () => {
      stripePromise.then(() => {}).catch(() => {});
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const id = window.requestIdleCallback(warm, { timeout: 800 });
      return () => window.cancelIdleCallback?.(id);
    }

    const t = setTimeout(warm, 250);
    return () => clearTimeout(t);
  }, []);

  const handleBackClick = () => {
    logEventSafe('click_back', {
      page_path: location.pathname
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) return;

      try {
        await currentUser.getIdToken(true);
        const { claims } = await currentUser.getIdTokenResult();
        setUserPlan(claims.stripeRole || 'standard');
      } catch (err) {
        console.error('Error fetching stripeRole claim:', err);
        setUserPlan('standard');
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const userData = userDoc.exists() ? userDoc.data() : {};
        setUserRole(userData.role || null);
        if (userData.role === 'manager' && userData.orgId) {
          setOrgId(userData.orgId);
        } else {
          setOrgId('');
        }
      } catch (err) {
        console.error('Error fetching user role/orgId:', err);
        setUserRole(null);
        setOrgId('');
      }
    });

    return unsubscribe;
  }, [auth]);

  useEffect(() => {
    if (userRole !== 'manager' || !orgId) return;
    const orgRef = doc(db, 'organizations', orgId);
    const unsubscribe = onSnapshot(orgRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setOrgMemberCount(data.memberCount || 1);
      }
    });
    return unsubscribe;
  }, [userRole, orgId]);

  const waitForCheckoutSession = useCallback((checkoutSessionRef, timeoutMs = 20000) => {
    return new Promise((resolve, reject) => {
      let done = false;

      const timeout = setTimeout(() => {
        if (done) return;
        done = true;
        unsubscribe?.();
        reject(new Error('Timed out waiting for Stripe Checkout session.'));
      }, timeoutMs);

      const unsubscribe = onSnapshot(
        checkoutSessionRef,
        (snap) => {
          const data = snap.data();

          if (data?.error) {
            if (done) return;
            done = true;
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error?.message || 'Stripe checkout session error.'));
            return;
          }

          if (data?.url) {
            if (done) return;
            done = true;
            clearTimeout(timeout);
            unsubscribe();
            resolve({ url: data.url, sessionId: data.sessionId || null });
            return;
          }

          if (data?.sessionId) {
            if (done) return;
            done = true;
            clearTimeout(timeout);
            unsubscribe();
            resolve({ url: null, sessionId: data.sessionId });
          }
        },
        (err) => {
          if (done) return;
          done = true;
          clearTimeout(timeout);
          unsubscribe?.();
          reject(err);
        }
      );
    });
  }, []);

  const tryCallableCheckoutFastPath = useCallback(async ({ priceId, quantity }) => {
    const fn = httpsCallable(functions, 'createCheckoutSession');
    const payload = {
      priceId,
      quantity: quantity ?? 1,
      successUrl: window.location.origin + '/account',
      cancelUrl: window.location.origin + '/pricing',
    };

    const result = await fn(payload);
    const url = result?.data?.url;

    if (!url) {
      throw new Error('Callable did not return a checkout URL.');
    }

    window.location.assign(url);
  }, []);

  const handleSubscribe = async () => {
    if (!user) {
      window.location.href = '/LoginPage?redirect=/pricing';
      return;
    }

    logEventSafe('click_subscribe', {
      plan: 'Plus',
      page_path: location.pathname
    });

    if (loadingAction) return;
    setLoadingAction('plus');

    const priceId = 'price_1R5UqJBqlgUftOgOpUM3PyO1';

    try {
      await tryCallableCheckoutFastPath({ priceId, quantity: 1 });
    } catch (fastErr) {
      console.warn('Fast checkout path failed, falling back to Firestore trigger:', fastErr);

      try {
        const checkoutSessionRef = await addDoc(
          collection(db, 'customers', user.uid, 'checkout_sessions'),
          {
            price: priceId,
            success_url: window.location.origin + '/account',
            cancel_url: window.location.origin + '/pricing',
          }
        );

        const { url, sessionId } = await waitForCheckoutSession(checkoutSessionRef);

        if (url) {
          window.location.assign(url);
          return;
        }

        if (sessionId) {
          const stripe = await stripePromise;
          await stripe.redirectToCheckout({ sessionId });
          return;
        }

        throw new Error('No checkout URL or sessionId returned.');
      } catch (fallbackErr) {
        console.error('Error creating checkout session:', fallbackErr);
        alert('Failed to start checkout. Please try again.');
        setLoadingAction(null);
      }
    }
  };

  const handleSubscribeTeams = async () => {
    if (!user) {
      window.location.href = '/LoginPage?redirect=/pricing';
      return;
    }

    logEventSafe('click_subscribe', {
      plan: 'Teams',
      seats: orgMemberCount,
      page_path: location.pathname
    });

    if (loadingAction) return;
    setLoadingAction('teams');

    const priceId = 'price_1RikcyBqlgUftOgOViLv3A35';

    try {
      await tryCallableCheckoutFastPath({ priceId, quantity: orgMemberCount });
    } catch (fastErr) {
      console.warn('Fast checkout path failed, falling back to Firestore trigger:', fastErr);

      try {
        const checkoutSessionRef = await addDoc(
          collection(db, 'customers', user.uid, 'checkout_sessions'),
          {
            price: priceId,
            quantity: orgMemberCount,
            success_url: window.location.origin + '/account',
            cancel_url: window.location.origin + '/pricing',
          }
        );

        const { url, sessionId } = await waitForCheckoutSession(checkoutSessionRef);

        if (url) {
          window.location.assign(url);
          return;
        }

        if (sessionId) {
          const stripe = await stripePromise;
          await stripe.redirectToCheckout({ sessionId });
          return;
        }

        throw new Error('No checkout URL or sessionId returned.');
      } catch (fallbackErr) {
        console.error('Error creating team checkout session:', fallbackErr);
        alert('Failed to start checkout. Please try again.');
        setLoadingAction(null);
      }
    }
  };

  const handleManageSubscription = async () => {
    logEventSafe('click_manage_subscription', {
      page_path: location.pathname
    });

    if (!user || loadingAction) return;

    setLoadingAction('portal');

    try {
      const createPortalLink = httpsCallable(functions, 'ext-firestore-stripe-payments-createPortalLink');
      const result = await createPortalLink({ returnUrl: window.location.origin + '/account' });

      if (result.data?.url) {
        window.location.assign(result.data.url);
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Error creating portal link:', err);
      alert('Failed to open customer portal. Please contact support.');
      setLoadingAction(null);
    }
  };

  const isLoadingPlus = loadingAction === 'plus';
  const isLoadingTeams = loadingAction === 'teams';
  const isLoadingPortal = loadingAction === 'portal';

  return (
    <PageShell>
      <div className="py-4">
        <PageBackLink onClick={handleBackClick} />
      </div>

      <motion.div
        className="flex flex-col items-center justify-center px-4 pb-12 sm:px-6 lg:px-8"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="mb-10 mx-auto max-w-3xl text-center" variants={itemVariants}>
          <h1 className="mb-4 text-4xl font-bold text-white animate-gradient-text">Choose Your Plan</h1>
          <p className="text-lg text-gray-300">
            Whether for personal use or enhancing the productivity of your team,
            Toolip has a plan that suits your needs. Start free and upgrade anytime!
          </p>
        </motion.div>

        <motion.div className="mb-12 mx-auto w-full desktop-page-width-xl" variants={containerVariants}>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 2xl:gap-8">
            <motion.div
              className="flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-lg transition-shadow hover:shadow-2xl select-none"
              variants={itemVariants}
            >
              <h2 className="mb-2 text-2xl font-semibold text-white">Free</h2>
              <p className="mb-4 text-center text-gray-400">Get started at no cost</p>
              <ul className="mb-6 space-y-2 text-center text-gray-300">
                <li className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
                  <span>All basic tools</span>
                </li>
                <li className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
                  <span>Standard customization</span>
                </li>
              </ul>
              <div className="mt-auto">
                <Link to="/get-started" className="rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90">
                  Get Started
                </Link>
              </div>
            </motion.div>

            <motion.div
              className={`relative flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-lg transition-shadow hover:shadow-2xl select-none ${userRole !== 'manager' ? 'ring-1 ring-[#2563eb]' : ''}`}
              variants={itemVariants}
            >
              {userRole !== 'manager' && (
                <span className="absolute right-3 top-3 rounded-full bg-[#2563eb] px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Recommended
                </span>
              )}
              <h2 className="mb-2 text-2xl font-semibold text-white">Plus</h2>
              <p className="mb-2 text-3xl font-bold text-white">
                $4.50 <span className="text-base font-medium text-gray-400">/month</span>
              </p>
              <p className="mb-4 text-center text-gray-400">Unlock premium features</p>
              <ul className="mb-6 space-y-2 text-center text-gray-300">
                <li className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
                  <span>All standard and premium tools</span>
                </li>
              </ul>
              <div className="mt-auto">
                {userRole === 'manager' ? (
                  <button disabled className="cursor-not-allowed rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white opacity-50 transition-colors">
                    Not available for teams
                  </button>
                ) : userPlan === 'standard' ? (
                  <button onClick={handleSubscribe} disabled={!!loadingAction} className="rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90">
                    {isLoadingPlus ? 'Opening...' : 'Subscribe'}
                  </button>
                ) : (
                  <button onClick={handleManageSubscription} disabled={!!loadingAction} className="rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90">
                    {isLoadingPortal ? 'Opening...' : 'Manage Subscription'}
                  </button>
                )}
              </div>
            </motion.div>

            <motion.div
              className={`relative flex flex-col items-center rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-lg transition-shadow hover:shadow-2xl select-none ${userRole === 'manager' ? 'ring-1 ring-[#2563eb]' : ''}`}
              variants={itemVariants}
            >
              {userRole === 'manager' && (
                <span className="absolute right-3 top-3 rounded-full bg-[#2563eb] px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Recommended
                </span>
              )}
              <h2 className="mb-2 text-2xl font-semibold text-white">Teams</h2>
              <p className="mb-2 text-3xl font-bold text-white">
                $6.50 <span className="text-base font-medium text-gray-400">/user</span>
              </p>
              <p className="mb-4 text-center text-gray-400">Custom solutions for your team</p>
              <ul className="mb-6 space-y-2 text-center text-gray-300">
                <li className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
                  <span>Easy Team Management</span>
                </li>
                <li className="flex items-center justify-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-blue-300" />
                  <span>Priority support</span>
                </li>
              </ul>
              <div className="mt-auto">
                {userRole === 'manager' ? (
                  userPlan === 'teams' ? (
                    <button onClick={handleManageSubscription} disabled={!!loadingAction} className="rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90">
                      {isLoadingPortal ? 'Opening...' : 'Manage Subscription'}
                    </button>
                  ) : (
                    <button onClick={handleSubscribeTeams} disabled={!!loadingAction || orgMemberCount < 1} className="cursor-pointer rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50">
                      {isLoadingTeams ? 'Opening...' : `Subscribe (${orgMemberCount} seats)`}
                    </button>
                  )
                ) : (
                  <Link to="/TeamInfo" className="rounded-xl bg-[#2563eb] px-4 py-2 font-semibold text-white transition-colors hover:opacity-90">
                    Learn More
                  </Link>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div className="mx-auto desktop-page-width-lg rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-lg select-none" variants={itemVariants}>
          <h2 className="mb-6 text-2xl font-semibold text-white">Frequently Asked Questions</h2>
          <div className="space-y-6 text-gray-300">
            <div>
              <p className="mb-1 font-semibold">1. What forms of payment do you accept?</p>
              <p>We accept all major credit cards. Our checkout is powered by Stripe, ensuring your payment data is secure.</p>
            </div>
            <div>
              <p className="mb-1 font-semibold">2. Can I cancel or change my plan anytime?</p>
              <p>Absolutely. You can cancel plans at any point. No contracts, no hidden fees.</p>
            </div>
            <div>
              <p className="mb-1 font-semibold">3. Is my data safe?</p>
              <p>Your data is secure and stored on reliable infrastructure. We also collect minimal data from users to ensure your information is private.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageShell>
  );
};

export default Pricing;
